/**
 * Hook for managing chat polling logic.
 */

import { useRef, useCallback, useEffect } from 'react';
import { useLazyTranslucenceChatTaskStatusQuery } from '@app/api/carApi.manual';
import type { TranslucenceChatMessageResponse } from '@app/api/carApi.manual';
import { POLLING_INTERVAL_MS, POLLING_TIMEOUT_MS } from '../constants/chatConstants';
import { createErrorMessage } from '../utils/messageHelpers';

export interface UseChatPollingCallbacks {
  onSuccess: (result: TranslucenceChatMessageResponse, taskId: string) => void;
  onFailure: (errorMessage: string) => void;
  onError: (errorMessage: string) => void;
  onTimeout: () => void;
  isRequestProcessed: (requestId: string | null) => boolean;
  getCurrentRequestId: () => string | null;
}

export interface UseChatPollingReturn {
  startPolling: (taskId: string) => void;
  stopPolling: () => void;
  isPolling: () => boolean;
}

/**
 * Hook for managing task status polling.
 * 
 * @param planResultRootId - Plan result root ID
 * @param planResultTypeId - Plan result type ID
 * @param callbacks - Callbacks for handling polling events
 * @returns Polling control functions
 */
export function useChatPolling(
  planResultRootId: string,
  planResultTypeId: string,
  callbacks: UseChatPollingCallbacks
): UseChatPollingReturn {
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [getTaskStatus] = useLazyTranslucenceChatTaskStatusQuery();

  /**
   * Stop polling and clear intervals/timeouts.
   */
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  }, []);

  /**
   * Start polling task status.
   */
  const startPolling = useCallback(
    (taskId: string) => {
      // Clear any existing polling
      stopPolling();

      // Set timeout for polling
      pollingTimeoutRef.current = setTimeout(() => {
        stopPolling();
        callbacks.onTimeout();
      }, POLLING_TIMEOUT_MS);

      // Start polling interval
      pollingIntervalRef.current = setInterval(async () => {
        try {
          const statusResult = await getTaskStatus({
            planResultRootId,
            planResultTypeId,
            taskId,
          }).unwrap();

          if (statusResult.status === 'SUCCESS') {
            stopPolling();
            
            // Only process if we haven't already processed via SSE COMPLETE message
            const requestId = callbacks.getCurrentRequestId();
            if (requestId && callbacks.isRequestProcessed(requestId)) {
              // Already processed via SSE, just stop loading
              return;
            }
            
            if (statusResult.result) {
              callbacks.onSuccess(statusResult.result, taskId);
            }
          } else if (statusResult.status === 'FAILURE') {
            stopPolling();
            const errorMessage = statusResult.error || 'Task execution failed';
            callbacks.onFailure(errorMessage);
          }
          // If PENDING, continue polling (do nothing)
        } catch (err: unknown) {
          stopPolling();
          const errorMessage =
            (err as { data?: { error?: string }; message?: string })?.data?.error ||
            (err as { message?: string })?.message ||
            'Failed to check task status';
          callbacks.onError(errorMessage);
        }
      }, POLLING_INTERVAL_MS);
    },
    [planResultRootId, planResultTypeId, getTaskStatus, stopPolling, callbacks]
  );

  /**
   * Check if polling is currently active.
   */
  const isPolling = useCallback(() => {
    return pollingIntervalRef.current !== null;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    startPolling,
    stopPolling,
    isPolling,
  };
}

